<template>
  <Alert
    v-if="$props.showAlert"
    :message="t('LIMIT_INCREASE.ERROR_TRY_AGAIN')"
    alertIcon="fa fa-exclamation-triangle"
    :closable="true"
    alertType="alert-danger"
    @onClose="dismissAlert"
  />
  <main>
    <h1 class="pt-2">
      {{ t("SHARED.CORE_APP_ONBOARDING.CREDIT_PULL_HEADER") }}
    </h1>
    <p class="mt-4">
      {{ t("SHARED.CORE_APP_ONBOARDING.CREDIT_PULL_DESCRIPTION") }}
    </p>
    <div
      class="w-100 auth-credit-doc"
      id="creditpull-show-document"
      @click="() => (showModal = true)"
    >
      <hr class="m-0 p-0 fw-bolder" />
      <p class="my-3">
        {{ t("SHARED.CORE_APP_ONBOARDING.CREDIT_PULL_TITLE") }}
        <i class="fa fa-chevron-right float-end"></i>
      </p>
      <hr class="m-0 p-0 fw-bolder" />
    </div>
    <h6 class="terms-label mt-5">{{ t("SHARED.SIGNHERE") }}</h6>
    <VueSignaturePad
      width="100%"
      height="125px"
      ref="signatureStepPadRef"
      class="signature-pad"
      :options="{ onEnd: () => emit('signature-drawn') }"
      id="creditpull-accept-signature-pad"
    />
    <div class="d-flex justify-content-between">
      <h6
        class="text-right mt-3 ms-auto me-0"
        @click="clearSignature"
        style="color: #2c7040"
      >
        {{ t("SHARED.CLEAR") }}
      </h6>
    </div>
    <Button
      :click-handler="acceptBtn"
      :disabled="acceptDisabled"
      class="accept-btn position-absolute w-100 start-0 px-3"
      id="creditpull-accept-button"
    >
      {{ t("SHARED.CORE_APP_ONBOARDING.I_ACCEPT") }}
    </Button>
  </main>

  <Modal :show="showModal">
    <template #header>
      <div class="d-flex flex-row">
        <h5 class="text-black fw-bolder text-start" id="modal-title-header">
          {{ t("SHARED.CORE_APP_ONBOARDING.CREDIT_PULL_TITLE") }}
        </h5>
        <span class="ms-auto me-0 right" @click="showModal = false"
          ><i class="fa fa-times" style="font-size: larger !important"></i
        ></span>
      </div>
    </template>
    <template #body class="mt-2">
      <div
        ref="docRef"
        id="modal-body"
        class="content p-2 overflow-auto c1c"
        style="scale: 0.8"
        v-html="$props.docHtml"
      ></div>
    </template>
    <template #footer>
      <Button
        id="modal-btn-cta"
        class="w-100 mt-3 text-center fw-bold"
        :click-handler="modalScrollToBottomModal"
      >
        {{ t("SHARED.GOTOBOTTOM") }}
      </Button>
    </template>
  </Modal>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from "vue";
import { setLanguage } from "@/apps/core-app-onboarding/helpers/translations.helper";
import Button from "@/apps/core-app-onboarding/components/Button.vue";
import { useI18n } from "vue-i18n";
import { VueSignaturePad } from "vue-signature-pad";
import Modal from "@/apps/credit-line/components/Modal.vue";
import Alert from "@/shared-components/common/Alert.vue";
import {
  stopAndSendCredolabFrontendData,
  sendCredolabDeviceReport,
} from "@/shared-services/credolab-api.service.ts";

export default defineComponent({
  name: "CreditPullDisplay",
  components: {
    Modal,
    Button,
    VueSignaturePad,
    Alert,
  },
  props: {
    docHtml: {
      type: String as PropType<string>,
      required: true,
    },
    acceptDisabled: {
      type: Boolean,
      required: true,
      default: true,
    },
    showAlert: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  emits: ["accept", "signature-drawn", "signature-cleared", "alert-closed"],
  beforeMount() {
    setLanguage(this);
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const showModal = ref<boolean>(false);
    const signatureStepPadRef = ref();
    const docRef = ref();
    try {
      sendCredolabDeviceReport({ caller: "CreditPullDisplay setup" });
    } catch (error) {
      console.log(error);
    }

    const clearSignature = () => {
      signatureStepPadRef.value.clearSignature();
      emit("signature-cleared");
    };

    const acceptBtn = async () => {
      const signature = signatureStepPadRef.value
        .saveSignature()
        .data.replace("data:image/png;base64,", "");
      try {
        stopAndSendCredolabFrontendData({
          caller: "CreditPullDisplay acceptButtonPressed",
        });
      } catch (error) {
        console.log(error);
      }
      emit("accept", signature);
    };

    const modalScrollToBottomModal = () => {
      docRef.value.scrollTop = docRef.value.scrollHeight;
    };

    const dismissAlert = () => {
      emit("alert-closed");
    };

    return {
      t,
      docRef,
      showModal,
      signatureStepPadRef,
      clearSignature,
      acceptBtn,
      modalScrollToBottomModal,
      emit,
      dismissAlert,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

main h1 {
  color: var(--Typography-Text-1, #111);
  font-feature-settings: "liga" off, "clig" off;
  font-family: Nunito;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 700;
  line-height: 1.75rem; /* 140% */
}

main p {
  color: #19191b;
  font-feature-settings: "liga" off, "clig" off;

  /* text-regular */
  font-family: Nunito;
  font-size: 0.9375rem;
  font-style: normal;
  font-weight: 500;
  line-height: 1.5rem; /* 160% */
}

.auth-credit-doc {
  & p {
    & i {
      margin-top: 1%;
    }
    color: var(--main-primary-500, #2c7040);
    font-feature-settings: "liga" off, "clig" off;

    /* text-bold */
    font-family: Nunito;
    font-size: 0.9375rem;
    font-style: normal;
    font-weight: 800;
    line-height: 1.5rem; /* 160% */
  }
}

.accept-btn {
  bottom: 2%;
}

.signature-pad {
  border-radius: 25px;
  border-color: $pj-color-light-gray;
  border-width: thin;
  border-style: solid;
}

#modal-title-header h5 {
  font-style: normal;
  font-family: Nunito;
  font-size: large;
}

#modal-body {
  max-height: 65vh !important;
}
</style>
